import { useEffect } from 'react';

export type PageMeta = {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  /** One Schema.org entity, or several emitted as a single JSON-LD array. */
  jsonLd: object | object[];
  /** `<html lang>` for the page (e.g. `en`, `uk`). */
  htmlLang?: string;
  /** Open Graph locale, underscore form (e.g. `en_US`). */
  ogLocale?: string;
  /** Open Graph alternate locale for the other language. */
  ogLocaleAlternate?: string;
  /** hreflang alternates emitted as `<link rel="alternate" hreflang>`. */
  alternates?: Array<{ hreflang: string; href: string }>;
};

// Manage page meta via head mutation. No react-helmet in the project; for the
// handful of public landing routes the direct approach is enough. Attributes
// that exist in index.html are updated in place and restored on unmount (so SPA
// navigation back to other routes keeps the default site-wide meta). Nodes that
// don't exist site-wide (JSON-LD, hreflang links, og:locale:alternate) are
// created fresh, tagged `data-page-meta`, and removed on unmount. They're also
// created via createElement + textContent / setAttribute (never React inline
// HTML) to stay clear of the dangerouslySetInnerHTML security hook.
export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevLang = document.documentElement.lang;
    const updates: Array<{ selector: string; attr: string; prev: string | null; next: string }> = [];

    const set = (selector: string, attr: string, value: string) => {
      const el = document.head.querySelector(selector);
      if (!el) return;
      updates.push({ selector, attr, prev: el.getAttribute(attr), next: value });
      el.setAttribute(attr, value);
    };

    document.title = meta.title;
    if (meta.htmlLang) document.documentElement.lang = meta.htmlLang;
    set('meta[name="description"]', 'content', meta.description);
    set('link[rel="canonical"]', 'href', meta.canonical);
    set('meta[property="og:title"]', 'content', meta.ogTitle);
    set('meta[property="og:description"]', 'content', meta.ogDescription);
    set('meta[property="og:image"]', 'content', meta.ogImage);
    set('meta[property="og:url"]', 'content', meta.ogUrl);
    if (meta.ogLocale) set('meta[property="og:locale"]', 'content', meta.ogLocale);
    set('meta[name="twitter:title"]', 'content', meta.ogTitle);
    set('meta[name="twitter:description"]', 'content', meta.ogDescription);
    set('meta[name="twitter:image"]', 'content', meta.ogImage);

    // Drop any prerendered (SSG) or prior-route managed nodes so hydration and
    // SPA navigation don't double-emit JSON-LD / stack stale hreflang links.
    document.head.querySelectorAll('[data-page-meta]').forEach((node) => node.remove());

    const managed: Element[] = [];
    const addManaged = (el: Element) => {
      el.setAttribute('data-page-meta', '1');
      document.head.appendChild(el);
      managed.push(el);
    };

    const jsonLdEl = document.createElement('script');
    jsonLdEl.type = 'application/ld+json';
    jsonLdEl.textContent = JSON.stringify(meta.jsonLd);
    addManaged(jsonLdEl);

    for (const alt of meta.alternates ?? []) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alt.hreflang);
      link.setAttribute('href', alt.href);
      addManaged(link);
    }

    if (meta.ogLocaleAlternate) {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:locale:alternate');
      m.setAttribute('content', meta.ogLocaleAlternate);
      addManaged(m);
    }

    return () => {
      document.title = prevTitle;
      document.documentElement.lang = prevLang;
      for (const u of updates) {
        const el = document.head.querySelector(u.selector);
        if (el && u.prev !== null) el.setAttribute(u.attr, u.prev);
      }
      managed.forEach((el) => el.remove());
    };
  }, [meta]);
}
